import type { Effect } from '@unifyapps/defs/types/effectObject';
import type { EFFECT_TYPES } from '@unifyapps/defs/types/effectObject/constants';

export type ShadowEffect = Extract<
  Effect,
  { type: typeof EFFECT_TYPES.DROP_SHADOW | typeof EFFECT_TYPES.INNER_SHADOW }
>;

export type ResolvedShadowAxes = Pick<ShadowEffect, 'x' | 'y' | 'blur' | 'spread' | 'color'>;

// Expression mode: the JS-toggle UI persists the whole-shadow binding under
// `effect.value`. The compute pipeline resolves `{{...}}` strings inline, so by
// render time `effect.value` is the resolved payload (per the editor's tooltip
// `SHADOW_EXAMPLE_SHAPE` — `{ x, y, blur, spread, color }`). Each axis falls
// back to the per-axis field when the binding shape omits it, so partial drafts
// don't blank the shadow mid-edit. TS still types `value` as `string`, so the
// shape check happens at runtime. Shared by the web (`getEffectsStyle`) and
// native (`getNativeEffectsDescriptor`) renderers so axis resolution can't drift.
export const getShadowAxes = (effect: ShadowEffect): ResolvedShadowAxes => {
  if (effect.valueType === 'expression' && effect.value && typeof effect.value === 'object') {
    const expr = effect.value as Partial<ResolvedShadowAxes>;
    return {
      x: expr.x ?? effect.x,
      y: expr.y ?? effect.y,
      blur: expr.blur ?? effect.blur,
      spread: expr.spread ?? effect.spread,
      color: expr.color ?? effect.color,
    };
  }
  return {
    x: effect.x,
    y: effect.y,
    blur: effect.blur,
    spread: effect.spread,
    color: effect.color,
  };
};
